import { I18n } from "@assets/strings";
import { uniq } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useSuggestedTagsByValue } from "@domain/tags/hook";
import { useOutsideAlerter } from "@utils/hooks";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Row } from "@ui/components/layout";
import { ReactTagInput } from "./internalLogic";
import "@ui/components/inputs/tagsInput/internalLogic/styles/index.scss";
import "./style.css";

const INITIAL_TAGS_COUNT = 10;

interface TagsInputProps extends React.HTMLAttributes<HTMLDivElement> {
	tags: string[];
	setTags: (newTags: string[]) => void;
	validator?: (value: string) => boolean;
	forbidDupplicates?: boolean;
	placeHolderFirstWord?: string;
	suggestionsDisabled?: boolean;
}

const trimTags = (tags: string[]): string[] => {
	return tags.map((tag) => tag.trimEnd());
};

export const TagsInput: React.FC<TagsInputProps> = ({
	tags: source,
	suggestionsDisabled = false,
	setTags: emitTags,
	validator,
	placeHolderFirstWord,
	forbidDupplicates = false,
	...htmlProps
}) => {
	const [input, setInput] = useState<string>("");
	const { result: suggestions, loading } = !suggestionsDisabled
		? useSuggestedTagsByValue(input)
		: { result: [], loading: false };
	const wrapperRef = useRef(null);
	useOutsideAlerter(wrapperRef, () => setFocused(false));
	const [tags, setTags] = useState(source && source.length > 0 ? source : []);
	const [focused, setFocused] = useState(false);
	const getSuggestions = (): string[] => {
		const newSuggestions: string[] = [];
		suggestions.filter(String).forEach((sg) => {
			if (sg.content && !tags.includes(sg.content)) newSuggestions.push(sg.content);
		});
		return newSuggestions;
	};

	const getPlaceHolder = (): string => {
		return placeHolderFirstWord
			? I18n.t("add_tags")
					.replaceAll("Tags", placeHolderFirstWord)
					.replaceAll("tags", placeHolderFirstWord.toLowerCase())
			: I18n.t("add_tags");
	};

	const [stringifiedSuggestions, setStringifiedSuggestions] = useState(getSuggestions());

	useEffect(() => {
		setStringifiedSuggestions(getSuggestions());
	}, [loading]);

	return (
		<Container ref={wrapperRef}>
			<ReactTagInput
				{...htmlProps}
				tags={tags}
				placeholder={getPlaceHolder()}
				maxTags={INITIAL_TAGS_COUNT}
				editable={true}
				readOnly={false}
				removeOnBackspace={true}
				onChange={(newTags: string[]) => {
					const trimmedTags = trimTags(forbidDupplicates ? uniq(newTags) : newTags);
					setTags(trimmedTags);
					emitTags(trimmedTags);
				}}
				onFocus={() => setFocused(true)}
				validator={validator}
				emitInput={(tag: string) => {
					setInput(tag);
				}}
				suggestions={stringifiedSuggestions.filter(String)}
			/>
			{focused && input.length > 1 && stringifiedSuggestions.filter(String).length > 0 && (
				<StyledSuggestions>
					{stringifiedSuggestions.filter(String).map((suggestion, index) => (
						<Suggestion
							onClick={() => {
								const newTags = [...tags, suggestion];
								const trimmedTags = trimTags(forbidDupplicates ? uniq(newTags) : newTags);
								setTags(trimmedTags);
								emitTags(trimmedTags);
								setFocused(false);
								setInput("");
								setStringifiedSuggestions([]);
							}}
							key={index}
						>
							{suggestion}
						</Suggestion>
					))}
				</StyledSuggestions>
			)}
		</Container>
	);
};
const Container = styled(RoundedBlock)`
	position: relative;
	background-color: inherit;
	border: unset;
	width: 100%;
`;

const StyledSuggestions = styled(RoundedBlock)`
	position: absolute;
	background-color: white;
	max-height: 183px;
	border-radius: 16px;
	height: -webkit-fit-content;
	height: -moz-fit-content;
	height: fit-content;
	padding: 7px;
	width: -webkit-fill-available;
	z-index: 10;
	overflow: auto;
	::-webkit-scrollbar {
		width: 8px;
		height: 4px;
	}

	/* Track */
	::-webkit-scrollbar-track {
		box-shadow: inset 0 0 5px grey;
		border-radius: 10px;
	}

	/* Handle */
	::-webkit-scrollbar-thumb {
		background: #007aff;
		border-radius: 10px;
	}

	/* Handle on hover */
	::-webkit-scrollbar-thumb:hover {
		background: #b30000;
	}
`;

const Suggestion = styled(Row)`
	margin-left: 15px;
	margin-bottom: 9px;
	font-family: Sora;
	font-style: normal;
	font-size: 15px;
	line-height: 20px;
	color: #13131a;
	padding: 2px;
	cursor: pointer;
`;

// ValidatorExample
// validator={(value) => {
// 	// Don't actually validate e-mails this way
// 	const isEmail = value.indexOf("@") !== -1;
// 	if (!isEmail) {
// 		alert("Please enter an e-mail address");
// 	}
// 	// Return boolean to indicate validity
// 	return isEmail;
// }}
